import { useState } from 'react';
import { api } from '../api';
import { useLive, ago } from '../live';
import { Mark } from '../App';
import SystemPrograms from './SystemPrograms';

/**
 * The garden: every node this account owns, and the way to adopt a new one.
 *
 * A node that has never been claimed prints a code on its console. Typing
 * that code here is the whole adoption flow — no pairing mode, no app, no
 * scanning. It works from a laptop while the board sits in a greenhouse.
 */
export default function Garden({ onOpenNode }) {
  const { data, error, loading, updatedAt, refresh } = useLive(() => api.nodes());
  const nodes = data?.nodes || [];

  // Systems ride alongside the nodes: the claim card offers them as homes
  // for a new controller, and the programs section below edits them.
  const systemsLive = useLive(() => api.systems());
  const systems = systemsLive.data?.systems || [];

  return (
    <div className="stack">
      <div className="spread">
        <div>
          <h1>Your garden</h1>
          <p className="muted" style={{ margin: 0 }}>
            {nodes.length === 0
              ? 'Nothing growing yet.'
              : `${nodes.length} ${nodes.length === 1 ? 'controller' : 'controllers'}, watching over things.`}
          </p>
        </div>
        <LiveBadge updatedAt={updatedAt} error={error} onRefresh={refresh} />
      </div>

      <ClaimCard onClaimed={() => { refresh(); systemsLive.refresh(); }}
                 systems={systems} />

      {loading && nodes.length === 0 && (
        <p className="faint">Looking for your controllers…</p>
      )}

      {!loading && nodes.length === 0 && (
        <div className="card empty">
          <div className="row" style={{ justifyContent: 'center', marginBottom: 'var(--s4)' }}>
            <Mark size={56} />
          </div>
          <h2>Plant the first one</h2>
          <p>
            Power up a controller and connect it to WiFi. It will show a six
            character code on its console. Enter that code above and it joins
            your garden.
          </p>
        </div>
      )}

      {nodes.length > 0 && (
        <div className="grid">
          {nodes.map((node) => (
            <NodeCard key={node.nodeId} node={node} onOpen={() => onOpenNode(node.nodeId)} />
          ))}
        </div>
      )}

      <SystemPrograms systems={systems} error={systemsLive.error}
                      loading={systemsLive.loading}
                      refresh={systemsLive.refresh} />
    </div>
  );
}

export function LiveBadge({ updatedAt, error, onRefresh }) {
  if (error) {
    return (
      <span className="pill pill-alert" title={error}>
        <span className="dot" /> Reconnecting
      </span>
    );
  }
  return (
    <span className="row" style={{ gap: 'var(--s3)' }}>
      <span className="pill pill-online"><span className="dot live" /> Live</span>
      <button className="btn btn-quiet btn-sm" onClick={onRefresh}>
        {updatedAt ? ago(updatedAt) : 'Refresh'}
      </button>
    </span>
  );
}

function NodeCard({ node, onOpen }) {
  const online = node.status === 'online';
  const sensors = (node.modules || []).filter((m) => m.kind === 'sensor');

  return (
    <div className="card stack" style={{ gap: 'var(--s4)', cursor: 'pointer' }}
         role="button" tabIndex={0} onClick={onOpen}
         onKeyDown={(e) => e.key === 'Enter' && onOpen()}>
      <div className="spread">
        <h3>{node.name}</h3>
        <span className={`pill ${online ? 'pill-online' : 'pill-offline'}`}>
          <span className={`dot ${online ? 'live' : ''}`} />
          {online ? 'Connected' : 'Asleep'}
        </span>
      </div>

      {sensors.length > 0 ? (
        <div className="row" style={{ gap: 'var(--s6)' }}>
          {sensors.slice(0, 3).map((m) => <Reading key={m.id} module={m} />)}
        </div>
      ) : (
        <p className="faint" style={{ margin: 0 }}>
          No sensors attached yet. Open it to wire one up.
        </p>
      )}

      <div className="faint">
        {node.hardware} · {(node.modules || []).length} modules · seen {ago(node.lastSeen)}
      </div>
    </div>
  );
}

/** pH and conductivity move in hundredths; everything else in tenths. */
export function decimalsFor(unit) {
  return unit === 'pH' || unit === 'mS/cm' ? 2 : 1;
}

/**
 * One sensor value.
 *
 * An uncalibrated probe shows its RAW count and says so. Presenting a
 * derived percentage from a probe that was never calibrated would be a
 * number that looks like a measurement and is not one.
 */
export function Reading({ module }) {
  const latest = module.latest;
  const hasValue = latest && latest.value !== null && latest.value !== undefined;
  const raw = latest?.raw;

  if (!latest) {
    return (
      <div className="reading">
        <span className="label">{module.label}</span>
        <span className="value" style={{ color: 'var(--ink-faint)' }}>–</span>
      </div>
    );
  }

  if (!hasValue) {
    return (
      <div className="reading raw" title="This probe has no calibration yet, so only the raw sensor count is meaningful.">
        <span className="label">{module.label}</span>
        <span className="value">{Math.round(raw)}</span>
        <span className="unit">raw · needs calibration</span>
      </div>
    );
  }

  const value = latest.value;
  const decimals = decimalsFor(module.unit);

  return (
    <div className="reading">
      <span className="label">{module.label}</span>
      <span className="value">
        {value.toFixed(decimals)}
        {module.unit && <span className="unit"> {module.unit}</span>}
      </span>
      {!latest.plausible && (
        <span className="unit" style={{ color: 'var(--alert)' }}>reading looks wrong</span>
      )}
    </div>
  );
}

function ClaimCard({ onClaimed, systems = [] }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [systemId, setSystemId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [claimed, setClaimed] = useState(null);

  // A controller outside a system runs no programs - no photoperiod, no
  // zones - so when exactly one system exists it is the obvious home.
  const effectiveSystemId = systemId !== ''
    ? Number(systemId)
    : (systems.length === 1 ? systems[0].id : null);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const node = await api.claimNode(code.trim().toUpperCase(), name || null,
                                       effectiveSystemId);
      setClaimed(node.name);
      setCode('');
      setName('');
      onClaimed();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={submit}>
      <div>
        <h3>Connect a controller</h3>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          Enter the code it showed when it came online.
        </p>
      </div>

      <div className="row" style={{ alignItems: 'flex-end', gap: 'var(--s4)' }}>
        <div style={{ flex: '0 0 220px' }}>
          <label htmlFor="code">Code</label>
          <input id="code" className="code-input" value={code} maxLength={6} required
                 onChange={(e) => setCode(e.target.value.toUpperCase())}
                 placeholder="XXXXXX" autoComplete="off" />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label htmlFor="nodename">Call it</label>
          <input id="nodename" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Kitchen herbs" />
        </div>
        {systems.length > 1 && (
          <div style={{ flex: '1 1 180px' }}>
            <label htmlFor="nodesystem">In which system</label>
            <select id="nodesystem" value={systemId}
                    onChange={(e) => setSystemId(e.target.value)}>
              <option value="">None yet</option>
              {systems.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}
        <button className="btn btn-primary" type="submit" disabled={busy || code.length < 6}>
          {busy ? 'Connecting…' : 'Connect'}
        </button>
      </div>

      {error && <div className="notice notice-error">{error}</div>}
      {claimed && !error && (
        <div className="notice notice-good">{claimed} joined your garden.</div>
      )}
    </form>
  );
}
